// src/client/screens/auth/KYCScreen.tsx
import { useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { NRC_REGEX } from '@/constants/config';
import { useAuth } from '@client/auth/useAuth';
import Button from '@client/components/Button';
import Card from '@client/components/Card';
import ScreenShell from '@client/components/ScreenShell';
import { useToast } from '@client/components/ToastProvider';
import { submitKyc } from '@client/services/kycService';

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(typeof r.result === 'string' ? r.result : '');
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

export default function KYCScreen() {
  const [front, setFront] = useState('');
  const [back, setBack] = useState('');
  const [nrc, setNrc] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { refresh } = useAuth();

  const onFront = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFront(await fileToDataUrl(f));
  };
  const onBack = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBack(await fileToDataUrl(f));
  };

  const submit = async () => {
    if (!NRC_REGEX.test(nrc.trim())) { toast.show('NRC format: 123456/78/9', 'error'); return; }
    if (!front || !back) { toast.show('Upload both NRC photos', 'error'); return; }
    setSubmitting(true);
    try {
      const r = await submitKyc({ frontDataUrl: front, backDataUrl: back, nrc });
      if (!r.ok) { toast.show('KYC submission failed', 'error'); return; }
      toast.show('KYC received — we will verify shortly', 'success');
      // TODO(sub-b): once the real submitKyc updates the profiles row,
      // refresh() will pull kyc_status='pending' so KycGuard does the right
      // thing on the next route. Today's stub doesn't change kyc_status,
      // so the borrower stays on the verified-fixture path during dev.
      await refresh();
      navigate(ROUTES.home, { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ScreenShell title="Verify your identity" withNav={false}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Card>
          <p className="small">We need a clear photo of the front and back of your NRC, plus your NRC number.</p>
        </Card>
        <Card title="NRC number">
          <input
            placeholder="123456/78/9"
            value={nrc}
            onChange={(e) => setNrc(e.target.value)}
            style={{ width: '100%', padding: 14, fontSize: 16, borderRadius: 12, border: '1px solid var(--c-border)' }}
          />
        </Card>
        <Card title="NRC front">
          <input type="file" accept="image/*" capture="environment" onChange={onFront} aria-label="NRC front photo" />
          {front && <img src={front} alt="NRC front preview" style={{ marginTop: 12, borderRadius: 12 }} />}
        </Card>
        <Card title="NRC back">
          <input type="file" accept="image/*" capture="environment" onChange={onBack} aria-label="NRC back photo" />
          {back && <img src={back} alt="NRC back preview" style={{ marginTop: 12, borderRadius: 12 }} />}
        </Card>
        <Button block size="lg" loading={submitting} onClick={submit}>Submit for verification</Button>
      </div>
    </ScreenShell>
  );
}
